/**
 * Shared Form Field Styles
 *
 * Task: T-P4-D2-F4b-4
 * Feature: P4-D2-F4 Web App Forms UI
 *
 * CSS Module for all form field components.
 * Uses design system CSS variables from globals.css.
 *
 * Key specs (from WEBAPP-DESIGN-SYSTEM.md):
 * - Input height: 48px minimum
 * - Border radius: --radius-md (14px)
 * - Touch targets: 44px minimum
 * - Focus: accent border + glow
 * - Error: error border + shake animation
 */

/* ═══════════════════════════════════════════════════════════
   FIELD GROUP CONTAINER
   ═══════════════════════════════════════════════════════════ */

.fieldGroup {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  animation: fieldEnter 0.5s var(--ease-out) backwards;
}

@keyframes fieldEnter {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
}

/* ═══════════════════════════════════════════════════════════
   LABEL
   ═══════════════════════════════════════════════════════════ */

.label {
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

.required {
  color: var(--color-accent-text);
  font-size: 16px;
}

/* ═══════════════════════════════════════════════════════════
   HELP TEXT
   ═══════════════════════════════════════════════════════════ */

.helpText {
  font-size: 12px;
  color: var(--color-text-muted);
  margin-top: -2px;
  overflow-wrap: break-word;
}

/* Consent checkbox: the statement is the question, so its help sits UNDER the row. */
.consentHelp {
  display: block;
  margin-top: 6px;
}

/* ═══════════════════════════════════════════════════════════
   INPUT (TEXT, EMAIL, NUMBER, DATE)
   ═══════════════════════════════════════════════════════════ */

.input {
  width: 100%;
  min-height: 48px;
  padding: var(--space-3) var(--space-4);
  border: 2px solid var(--color-bg-secondary);
  border-radius: var(--radius-md);
  background: var(--color-bg-primary);
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--color-text-primary);
  transition: all 0.2s var(--ease-out);
  outline: none;
}

.input::placeholder {
  color: var(--color-text-muted);
}

/* iOS renders the native value in its own box. Give that box the full inner
   height and center it instead of relying on the input's text baseline. */
.dateInput {
  height: 48px;
  padding-block: 0;
  appearance: none;
  -webkit-appearance: none;
}

.dateInput::-webkit-date-and-time-value {
  display: inline-flex;
  align-items: center;
  height: 100%;
  text-align: left;
}

.dateInput::-webkit-datetime-edit {
  display: inline-flex;
  align-items: center;
  padding: 0;
  height: 100%;
}

.input:hover:not(:focus):not(.hasError) {
  border-color: var(--color-text-muted);
}

.input:focus {
  border-color: var(--color-accent);
  box-shadow: var(--shadow-glow);
  background: var(--color-bg-elevated);
}

.input.hasError {
  border-color: var(--color-error);
  box-shadow: 0 0 0 4px rgba(242, 139, 130, 0.15);
}

.input.hasSuccess {
  border-color: var(--color-success);
}

/* ═══════════════════════════════════════════════════════════
   INPUT WITH ICON (EMAIL)
   ═══════════════════════════════════════════════════════════ */

.inputWithIcon {
  position: relative;
}

.inputWithIcon .input {
  padding-right: 44px;
}

.inputIcon {
  position: absolute;
  right: var(--space-3);
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  opacity: 0;
  transition: all 0.3s var(--ease-bounce);
  pointer-events: none;
}

.inputIcon.visible {
  opacity: 1;
}

.inputIcon.success {
  color: var(--color-success);
}

.inputIcon.error {
  color: var(--color-error);
}

/* ═══════════════════════════════════════════════════════════
   TEXTAREA
   ═══════════════════════════════════════════════════════════ */

.textarea {
  min-height: 100px;
  resize: none; /* grows with the answer (TextareaField fits height to content) */
  overflow: hidden;
  line-height: 1.5;
}

/* ═══════════════════════════════════════════════════════════
   CUSTOM SELECT DROPDOWN
   ═══════════════════════════════════════════════════════════ */

.selectContainer {
  position: relative;
}

.selectTrigger {
  width: 100%;
  min-height: 48px;
  padding: var(--space-3) var(--space-4);
  padding-right: 44px;
  border: 2px solid var(--color-bg-secondary);
  border-radius: var(--radius-md);
  background: var(--color-bg-primary);
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--color-text-primary);
  text-align: left;
  cursor: pointer;
  transition: all 0.2s var(--ease-out);
  outline: none;
  position: relative;
}

.selectTrigger.placeholder {
  color: var(--color-text-muted);
}

.selectTrigger:hover:not(.hasError):not(.isOpen) {
  border-color: var(--color-text-muted);
}

.selectTrigger.isOpen,
.selectTrigger:focus-visible {
  border-color: var(--color-accent);
  box-shadow: var(--shadow-glow);
  background: var(--color-bg-elevated);
}

.selectTrigger.hasError {
  border-color: var(--color-error);
  box-shadow: 0 0 0 4px rgba(242, 139, 130, 0.15);
}

/* Arrow icon */
.selectArrow {
  position: absolute;
  right: var(--space-4);
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  color: var(--color-text-muted);
  transition: transform 0.2s var(--ease-out), color 0.2s var(--ease-out);
  pointer-events: none;
}

.selectTrigger.isOpen .selectArrow,
.selectTrigger:focus-visible .selectArrow {
  color: var(--color-accent-text);
  transform: translateY(-50%) rotate(180deg);
}

/* Dropdown options list */
.selectDropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 240px;
  overflow-y: auto;
  background: var(--color-bg-elevated);
  border: 2px solid var(--color-accent);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  z-index: 100;
  opacity: 0;
  transform: translateY(-8px);
  pointer-events: none;
  transition: all 0.2s var(--ease-out);
}

.selectDropdown.isOpen {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

/* Individual option */
.selectOption {
  width: 100%;
  min-height: 44px;
  padding: var(--space-3) var(--space-4);
  border: none;
  background: transparent;
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--color-text-primary);
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
  display: flex;
  align-items: center;
}

.selectOption:hover,
.selectOption:focus-visible {
  background: var(--color-bg-secondary);
  outline: none;
}

.selectOption.isSelected {
  background: var(--color-accent-light);
  color: var(--color-accent-text);
  font-weight: 500;
}

.selectOption:first-child {
  border-radius: calc(var(--radius-md) - 2px) calc(var(--radius-md) - 2px) 0 0;
}

.selectOption:last-child {
  border-radius: 0 0 calc(var(--radius-md) - 2px) calc(var(--radius-md) - 2px);
}

.selectOption:only-child {
  border-radius: calc(var(--radius-md) - 2px);
}

/* Scrollbar styling for dropdown */
.selectDropdown::-webkit-scrollbar {
  width: 6px;
}

.selectDropdown::-webkit-scrollbar-track {
  background: transparent;
}

.selectDropdown::-webkit-scrollbar-thumb {
  background: var(--color-text-muted);
  border-radius: 3px;
}

/* Legacy native select - kept for backwards compatibility if needed */
.select {
  -webkit-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23A8A29E' stroke-width='2' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right var(--space-4) center;
  padding-right: 44px;
  cursor: pointer;
}

.select:focus {
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23E07A5F' stroke-width='2' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
}

/* ═══════════════════════════════════════════════════════════
   FIELD FOOTER (ERROR + COUNTER)
   ═══════════════════════════════════════════════════════════ */

.fieldFooter {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 20px;
}

.errorText {
  font-size: 12px;
  color: var(--color-error);
  display: flex;
  align-items: center;
  gap: var(--space-1);
  animation: errorShake 0.4s var(--ease-out);
}

.errorText svg {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

@keyframes errorShake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-4px); }
  40% { transform: translateX(4px); }
  60% { transform: translateX(-2px); }
  80% { transform: translateX(2px); }
}

/* ═══════════════════════════════════════════════════════════
   CHARACTER COUNTER
   ═══════════════════════════════════════════════════════════ */

.counter {
  font-size: 11px;
  font-family: var(--font-mono);
  color: var(--color-text-muted);
  margin-left: auto;
}

.counter.nearLimit {
  color: var(--color-accent-text);
}

.counter.atLimit {
  color: var(--color-error);
}

/* ═══════════════════════════════════════════════════════════
   CHECKBOX (SINGLE CONSENT)
   ═══════════════════════════════════════════════════════════ */

.checkboxGroup {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  cursor: pointer;
  min-height: 44px;
}

.checkboxInput {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.checkboxBox {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border: 2px solid var(--color-bg-secondary);
  border-radius: var(--radius-sm);
  background: var(--color-bg-primary);
  display: grid;
  place-items: center;
  transition: all 0.2s var(--ease-bounce);
  margin-top: 2px;
}

.checkboxBox svg {
  width: 14px;
  height: 14px;
  color: var(--color-text-primary);
  opacity: 0;
  transform: scale(0.5);
  transition: all 0.2s var(--ease-bounce);
}

.checkboxGroup:hover .checkboxBox {
  border-color: var(--color-accent);
}

.checkboxInput:checked + .checkboxBox {
  background: var(--color-accent);
  border-color: var(--color-accent);
}

.checkboxInput:checked + .checkboxBox svg {
  opacity: 1;
  transform: scale(1);
}

.checkboxInput:focus-visible + .checkboxBox {
  box-shadow: var(--shadow-glow);
}

.checkboxBox.hasError {
  border-color: var(--color-error);
}

.checkboxLabel {
  font-size: 14px;
  color: var(--color-text-primary);
  line-height: 1.5;
}

.checkboxLabel a {
  color: var(--color-accent-text);
  text-decoration: underline;
}

/* ═══════════════════════════════════════════════════════════
   CHECKBOX (MULTI-SELECT GROUP)
   ═══════════════════════════════════════════════════════════ */

.checkboxMultiGroup {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.checkboxOption {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  cursor: pointer;
  min-height: 44px;
}

/* ═══════════════════════════════════════════════════════════
   RADIO BUTTONS
   ═══════════════════════════════════════════════════════════ */

.radioGroup {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.radioOption {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  cursor: pointer;
  min-height: 44px;
}

.radioInput {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.radioBox {
  width: 24px;
  height: 24px;
  border: 2px solid var(--color-bg-secondary);
  border-radius: 50%;
  background: var(--color-bg-primary);
  display: grid;
  place-items: center;
  transition: all 0.2s var(--ease-bounce);
  flex-shrink: 0;
}

.radioBox::after {
  content: '';
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--color-accent);
  opacity: 0;
  transform: scale(0);
  transition: all 0.2s var(--ease-bounce);
}

.radioOption:hover .radioBox {
  border-color: var(--color-accent);
}

.radioInput:checked + .radioBox {
  border-color: var(--color-accent);
}

.radioInput:checked + .radioBox::after {
  opacity: 1;
  transform: scale(1);
}

.radioInput:focus-visible + .radioBox {
  box-shadow: var(--shadow-glow);
}

.radioBox.hasError {
  border-color: var(--color-error);
}

.radioLabel {
  font-size: 14px;
  color: var(--color-text-primary);
  line-height: 1.5;
}

/* form-embed D13 — a question set to Chips: one pill per option, wrapping onto as many rows as it needs
   (mockup form-builder-chips.html, the preview's chips). The radio input stays, hidden, for keys and readers.
   The pill (the website's choice part) carries the color and its words inherit it, so a site restyling the part
   restyles both. */
.chips {
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.chips .radioOption {
  gap: 0;
  padding: 9px 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-full);
  background: var(--color-bg-secondary);
  color: var(--color-text-secondary);
}

.chips .radioBox {
  display: none;
}

.chips .radioLabel {
  font-size: 13.5px;
  font-weight: 500;
  color: inherit;
}

.chips .chipOn {
  background: var(--color-accent-light);
  box-shadow: inset 0 0 0 1px var(--color-accent);
  color: var(--color-accent-text);
}

.chips .radioOption:has(.radioInput:focus-visible) {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

/* ═══════════════════════════════════════════════════════════
   CWR 2.5 (§3-E) — clientBooking field restyle.
   Scoped under the form root's data-form-appearance="clientBooking" marker
   (FormRenderer); the DEFAULT admin/legacy form pages are byte-for-byte
   untouched. Warm dark-inset primitives on injected client tokens, matching
   the booking-form mockup: labels sit ABOVE the inputs, fields/consent/radio
   are bordered rows.
   ═══════════════════════════════════════════════════════════ */

/* Every field label (incl. radio/checkbox question labels) sits muted and turns accent
   when its field is focused — the mockup's f-flabel color behavior, label kept ABOVE
   the box. */
:global([data-form-appearance='clientBooking']) .label {
  font-family: var(--font-body, 'Outfit', system-ui, sans-serif);
  font-size: 13.5px;
  font-weight: 500;
  color: var(--text-tertiary);
}
:global([data-form-appearance='clientBooking']) .fieldGroup:focus-within .label {
  color: var(--accent-hot);
}
:global([data-form-appearance='clientBooking']) .required {
  color: var(--accent);
  font-size: 14px;
}
/* Help text ("We'll send confirmations here", "this goes straight to …") flows BELOW
   the field (mockup .f-help): flex order pushes it under the input, footer last. */
:global([data-form-appearance='clientBooking']) .helpText {
  font-size: 11.5px;
  line-height: 1.4;
  color: var(--text-tertiary);
  order: 1;
  margin-top: 6px;
}
:global([data-form-appearance='clientBooking']) .fieldFooter {
  order: 2;
}

/* Text / email / number / date / textarea + the custom select trigger */
:global([data-form-appearance='clientBooking']) .input,
:global([data-form-appearance='clientBooking']) .selectTrigger {
  background: var(--bg-inset);
  border: 1px solid var(--text-muted);
  border-radius: 14px;
  color: var(--text-primary);
  /* iOS auto-zooms (and scroll-jumps) on focus for inputs < 16px — the proven rule the
     admin wizard already follows. Keep booking-form inputs at 16px. */
  font-size: 16px;
}
:global([data-form-appearance='clientBooking']) .input::placeholder {
  color: var(--text-muted);
}
/* No hover state on touch (WKWebView :hover sticks after a scroll-graze, "highlighting"
   a box the user never selected). These NON-hover rules pin the resting border and
   outrank the base .input/.selectTrigger :hover, so ONLY :focus (actual selection) highlights. */
:global([data-form-appearance='clientBooking']) .input:not(:focus):not(.hasError),
:global([data-form-appearance='clientBooking']) .selectTrigger:not(:focus-visible):not(.hasError):not(.isOpen) {
  border-color: var(--text-muted);
}
:global([data-form-appearance='clientBooking']) .input:focus,
:global([data-form-appearance='clientBooking']) .selectTrigger.isOpen,
:global([data-form-appearance='clientBooking']) .selectTrigger:focus-visible {
  border-color: rgba(var(--accent-rgb), 0.55);
  box-shadow: 0 0 18px rgba(var(--accent-rgb), 0.14);
  background: var(--bg-inset);
}
/* Native date input ignores width on iOS WebKit + adds an intrinsic picker width,
   so the field runs off-page. appearance:none + min-width:0 makes it honor the box
   (the value still shows and the wheel picker still opens on tap). */
:global([data-form-appearance='clientBooking']) .input[type='date'] {
  -webkit-appearance: none;
  appearance: none;
  min-width: 0;
}

/* Custom select dropdown */
:global([data-form-appearance='clientBooking']) .selectArrow {
  color: var(--accent);
}
:global([data-form-appearance='clientBooking']) .selectDropdown {
  background: var(--bg-card);
  border-color: rgba(var(--accent-rgb), 0.4);
}
:global([data-form-appearance='clientBooking']) .selectOption {
  color: var(--text-primary);
  background: transparent; /* outranks the base .selectOption:hover grey — no touch-hover bg */
}
:global([data-form-appearance='clientBooking']) .selectOption:focus-visible {
  background: rgba(var(--accent-rgb), 0.1);
}
:global([data-form-appearance='clientBooking']) .selectOption.isSelected {
  background: rgba(var(--accent-rgb), 0.16);
  color: var(--accent);
}

/* Consent checkbox → bordered inset row (mockup .consent) */
:global([data-form-appearance='clientBooking']) .checkboxGroup {
  padding: 11px 13px;
  border-radius: 14px;
  background: var(--bg-inset);
  border: 1px solid var(--text-muted);
}
/* .checkboxGroup-qualified so the resting border outranks the base .checkboxGroup:hover
   (no touch-hover accent); the :checked state below is the only affordance. */
:global([data-form-appearance='clientBooking']) .checkboxGroup .checkboxBox {
  width: 21px;
  height: 21px;
  border: 2px solid var(--text-muted);
  border-radius: 7px;
  background: transparent;
}
:global([data-form-appearance='clientBooking']) .checkboxInput:checked + .checkboxBox {
  background: var(--accent);
  border-color: var(--accent);
}
:global([data-form-appearance='clientBooking']) .checkboxInput:checked + .checkboxBox svg {
  color: var(--text-on-accent);
}
:global([data-form-appearance='clientBooking']) .checkboxLabel {
  font-size: 12.5px;
  color: var(--text-secondary);
}

/* Radio → stacked bordered inset rows (mockup .opt) */
:global([data-form-appearance='clientBooking']) .radioOption {
  padding: 11px 13px;
  border-radius: 14px;
  background: var(--bg-inset);
  border: 1px solid var(--text-muted);
}
/* .radioOption-qualified so the resting border outranks the base .radioOption:hover
   (no touch-hover accent); the :checked state below is the only affordance. */
:global([data-form-appearance='clientBooking']) .radioOption .radioBox {
  border: 2px solid var(--text-muted);
  background: transparent;
}
:global([data-form-appearance='clientBooking']) .radioInput:checked + .radioBox {
  border-color: var(--accent);
}
:global([data-form-appearance='clientBooking']) .radioInput:checked + .radioBox::after {
  background: var(--accent);
}
:global([data-form-appearance='clientBooking']) .radioLabel {
  color: var(--text-primary);
}

/* Chips (form-embed D13, mockup site-form.html): outlined pills; the chosen one filled with the accent. */
:global([data-form-appearance='clientBooking']) .chips .radioOption {
  min-height: 44px;
  padding: 10px 16px;
  border: 1px solid var(--text-muted);
  border-radius: 999px;
  background: transparent;
  color: var(--text-primary);
}
:global([data-form-appearance='clientBooking']) .chips .radioLabel {
  font-size: 15px;
  color: inherit;
}
:global([data-form-appearance='clientBooking']) .chips .chipOn {
  background: var(--accent);
  border-color: var(--accent);
  box-shadow: none;
  color: var(--bg-page);
}
:global([data-form-appearance='clientBooking']) .chips .chipOn .radioLabel {
  font-weight: 600;
}
:global([data-form-appearance='clientBooking']) .chips .radioOption:has(.radioInput:focus-visible) {
  outline-color: var(--accent);
}
